import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { GraduationCap, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8 text-center">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">Invalid Link</h1>
            <p className="font-body text-sm text-ink/60 mb-6">This password reset link is invalid or missing.</p>
            <Link to="/forgot-password">
              <Button className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm" data-testid="request-new-link-btn">
                Request New Reset Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8 sm:p-10">
          <div className="text-center mb-8">
            <GraduationCap className="w-10 h-10 text-cabernet mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-medium text-cabernet mb-2">
              {success ? 'Password Reset' : 'Set New Password'}
            </h1>
          </div>

          {success ? (
            <div className="text-center">
              <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-4" />
              <p className="font-body text-sm text-ink/60 mb-6" data-testid="reset-success-message">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm px-6 py-5" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm font-accent text-red-700" data-testid="reset-error">
                  {error}
                </div>
              )}
              <div>
                <Input
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm py-5"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
